import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Briefcase, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline',
  'list'
];

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    location: 'Allentown, PA',
    available_shifts: '',
    job_description: '',
    description: '',
    why_join_lhss: '',
    responsibilities: '',
    qualifications: '',
    additional_info: '',
    salary: '',
    active: true
  });
  const { toast } = useToast();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs`, {
        headers: getAuthHeaders()
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Jobs fetch error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load jobs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Convert old array format to HTML for backwards compatibility
  const arrayToHtml = (arr) => {
    if (!arr) return '';
    if (typeof arr === 'string') return arr; // Already HTML
    if (Array.isArray(arr)) {
      return '<ul>' + arr.map(item => `<li>${item}</li>`).join('') + '</ul>';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        title: formData.title,
        type: formData.type,
        location: formData.location,
        available_shifts: formData.available_shifts || '',
        job_description: formData.job_description || '',
        description: formData.description,
        why_join_lhss: formData.why_join_lhss || '',
        responsibilities: formData.responsibilities,
        qualifications: formData.qualifications,
        additional_info: formData.additional_info || '',
        salary: formData.salary,
        active: formData.active
      };

      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, jobData, {
          headers: getAuthHeaders()
        });
        toast({ title: 'Success', description: 'Job updated successfully' });
      } else {
        await axios.post(`${API}/admin/jobs`, jobData, {
          headers: getAuthHeaders()
        });
        toast({ title: 'Success', description: 'Job created successfully' });
      }
      fetchJobs();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save job',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;
    
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, {
        headers: getAuthHeaders()
      });
      toast({ title: 'Success', description: 'Job deleted successfully' });
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete job',
        variant: 'destructive'
      });
    }
  };

  const toggleActive = async (job) => {
    try {
      await axios.put(`${API}/admin/jobs/${job.id}`, { active: !job.active }, {
        headers: getAuthHeaders()
      });
      toast({ 
        title: 'Success', 
        description: `Job ${!job.active ? 'activated' : 'deactivated'} successfully` 
      });
      fetchJobs();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update job status',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      type: job.type,
      location: job.location,
      available_shifts: job.available_shifts || '',
      job_description: job.job_description || '',
      description: job.description || '',
      why_join_lhss: job.why_join_lhss || '',
      responsibilities: arrayToHtml(job.responsibilities),
      qualifications: arrayToHtml(job.qualifications),
      additional_info: job.additional_info || '',
      salary: job.salary,
      active: job.active
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingJob(null);
    setFormData({
      title: '',
      type: '',
      location: 'Allentown, PA',
      available_shifts: '',
      job_description: '',
      description: '',
      why_join_lhss: '',
      responsibilities: '',
      qualifications: '',
      additional_info: '',
      salary: '',
      active: true
    });
  };

  // Custom styles for Quill editors
  const quillStyle = {
    '.ql-editor': {
      minHeight: '120px'
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-jobs">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Job Listings Management</h2>
          <p className="text-gray-600 mt-2">Create and manage job postings ({jobs.length} jobs)</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600" data-testid="create-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
              <DialogDescription>
                {editingJob ? 'Update the job listing details below.' : 'Fill in the details to create a new job posting.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Direct Support Professional"
                    required
                    data-testid="job-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="type">Job Type *</Label>
                  <Input
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    placeholder="e.g., Full-Time, Part-Time, Seasonal, PRN"
                    required
                    data-testid="job-type-input"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    data-testid="job-location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="salary">Salary *</Label>
                  <Input
                    id="salary"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="e.g., $15 - $18/hour"
                    required
                    data-testid="job-salary-input"
                  />
                </div>
              </div>

              {/* Available Shifts - multiline text area */}
              <div>
                <Label htmlFor="available_shifts">Available Shifts</Label>
                <p className="text-sm text-gray-500 mb-2">Enter one shift per line (optional)</p>
                <Textarea
                  id="available_shifts"
                  value={formData.available_shifts}
                  onChange={(e) => setFormData({ ...formData, available_shifts: e.target.value })}
                  placeholder={"7:00 a.m.–3:00 p.m.\n3:00 p.m.–11:00 p.m.\n11:00 p.m.–7:00 a.m."}
                  className="min-h-[100px] resize-y"
                  data-testid="job-available-shifts-input"
                />
              </div>

              {/* Rich Text Sections - 6 separate editors */}
              <div className="space-y-6 border-t pt-6">
                
                {/* 1. Job Description (NEW - first field) */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">Job Description</Label>
                  <p className="text-sm text-gray-500 mb-2">General description of the position (optional)</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.job_description}
                      onChange={(value) => setFormData({ ...formData, job_description: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter a general job description..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-description-field-editor"
                    />
                  </div>
                </div>

                {/* 2. About This Role */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">About This Role *</Label>
                  <p className="text-sm text-gray-500 mb-2">Brief overview of the position</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) => setFormData({ ...formData, description: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Describe the role and its importance..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-about-role-editor"
                    />
                  </div>
                </div>

                {/* 3. Why Join LHSS */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">Why Join LHSS</Label>
                  <p className="text-sm text-gray-500 mb-2">Benefits and reasons to work here (optional)</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.why_join_lhss}
                      onChange={(value) => setFormData({ ...formData, why_join_lhss: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Share what makes LHSS a great place to work..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-why-join-editor"
                    />
                  </div>
                </div>

                {/* 4. Responsibilities */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">Responsibilities *</Label>
                  <p className="text-sm text-gray-500 mb-2">Key duties and tasks for this role</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.responsibilities}
                      onChange={(value) => setFormData({ ...formData, responsibilities: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="List the main responsibilities (use bullet points)..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-responsibilities-editor"
                    />
                  </div>
                </div>

                {/* 5. Qualifications */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">Qualifications *</Label>
                  <p className="text-sm text-gray-500 mb-2">Required skills and experience</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.qualifications}
                      onChange={(value) => setFormData({ ...formData, qualifications: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="List required qualifications (use bullet points)..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-qualifications-editor"
                    />
                  </div>
                </div>

                {/* 6. Additional Information */}
                <div className="editor-section">
                  <Label className="text-base font-semibold block mb-1">Additional Information</Label>
                  <p className="text-sm text-gray-500 mb-2">Any other details (optional)</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.additional_info}
                      onChange={(value) => setFormData({ ...formData, additional_info: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Add any additional information about the position..."
                      className="bg-white"
                      style={{ minHeight: '150px' }}
                      data-testid="job-additional-info-editor"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="job-active-checkbox"
                />
                <Label htmlFor="active">Active (visible to public)</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" data-testid="job-submit-btn">
                  {editingJob ? 'Update' : 'Create'} Job
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      ) : jobs.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent>
            <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Job Listings Yet</h3>
            <p className="text-gray-600 mb-4">Start by creating your first job posting</p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              Create First Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <Card key={job.id} className={`${!job.active ? 'opacity-60 bg-gray-50' : ''}`} data-testid={`job-card-${job.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Briefcase className="w-5 h-5 text-amber-500" />
                      <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                      {!job.active && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span>{job.type}</span>
                      <span>📍 {job.location}</span>
                      <span className="text-amber-600 font-medium">{job.salary}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(job)}
                      data-testid={`toggle-active-${job.id}`}
                    >
                      {job.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(job)}
                      data-testid={`edit-job-${job.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(job.id)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quill CSS overrides */}
      <style>{`
        .ql-container {
          min-height: 150px !important;
          font-size: 14px;
          background-color: white !important;
          border: 1px solid #e5e7eb !important;
        }
        .ql-editor {
          min-height: 150px !important;
          padding: 12px 15px;
          color: #1f2937 !important;
          background-color: white !important;
        }
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: italic;
        }
        .ql-toolbar {
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background: #f9fafb !important;
          border: 1px solid #e5e7eb !important;
          border-bottom: none !important;
        }
        .ql-container {
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }
        .ql-toolbar .ql-formats {
          margin-right: 12px;
        }
        .ql-snow .ql-picker {
          color: #374151;
        }
        .ql-snow .ql-stroke {
          stroke: #374151;
        }
        .ql-snow .ql-fill {
          fill: #374151;
        }
        .ql-snow.ql-toolbar button:hover,
        .ql-snow .ql-toolbar button:hover {
          color: #d97706;
        }
        .ql-snow.ql-toolbar button:hover .ql-stroke,
        .ql-snow .ql-toolbar button:hover .ql-stroke {
          stroke: #d97706;
        }
        .ql-snow.ql-toolbar button.ql-active,
        .ql-snow .ql-toolbar button.ql-active {
          color: #d97706;
        }
        .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .ql-snow .ql-toolbar button.ql-active .ql-stroke {
          stroke: #d97706;
        }
      `}</style>
    </div>
  );
};

export default AdminJobs;
